import { ARROW_DOWN, ARROW_UP } from "../utils/keys.js";
import { handleMenuArrowKeys } from "../utils/menuKeyNav.js";

const FOCUSABLE = "a[href], button:not([disabled])";
let instanceCount = 0;

export default class NavJump {
  constructor(el) {
    this.root = el;
    this.button = el.querySelector(".js--nav-jump-button");
    this.menu = el.querySelector(".js--nav-jump-menu");

    if (!this.button || !this.menu) return;

    this.isOpen = false;

    this.button.setAttribute("aria-expanded", "false");
    this.button.setAttribute(
      "aria-controls",
      this.menu.id || this.ensureMenuId(),
    );

    this.bindEvents();
  }

  ensureMenuId() {
    if (!this.menu.id) this.menu.id = `nav-jump-menu-${++instanceCount}`;
    return this.menu.id;
  }

  getFocusable() {
    return [...this.menu.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetWidth || el.offsetHeight,
    );
  }

  open(focusTarget) {
    this.isOpen = true;
    this.root.classList.add("nav-jump--is-open");
    this.button.setAttribute("aria-expanded", "true");
    document.dispatchEvent(new CustomEvent("nav-jump:open", { detail: this }));
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        (focusTarget ?? this.getFocusable()[0])?.focus(),
      ),
    );
  }

  close() {
    this.isOpen = false;
    this.root.classList.remove("nav-jump--is-open");
    this.button.setAttribute("aria-expanded", "false");
    this.button.focus();
  }

  bindEvents() {
    this.button.addEventListener("click", () => {
      this.isOpen ? this.close() : this.open();
    });

    this.button.addEventListener("keydown", (e) => {
      if (this.isOpen) return;
      const items = [...this.menu.querySelectorAll("a[href]")];
      if (!items.length) return;
      if (e.key === ARROW_DOWN) {
        e.preventDefault();
        this.open();
      } else if (e.key === ARROW_UP) {
        e.preventDefault();
        this.open(items[items.length - 1]);
      }
    });

    this.menu.querySelectorAll("a[href]").forEach((link) => {
      link.addEventListener("click", () => this.close());
    });

    document.addEventListener("keydown", (e) => {
      if (!this.isOpen) return;

      if (e.key === "Escape") {
        this.close();
        return;
      }

      if (e.key === "Tab") {
        const focusable = this.getFocusable();
        if (focusable.length < 2) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }

      handleMenuArrowKeys(e, {
        toggle: this.button,
        items: this.getFocusable(),
        wrap: true,
      });
    });

    document.addEventListener("click", (e) => {
      if (this.isOpen && !this.root.contains(e.target)) {
        this.close();
      }
    });

    document.addEventListener("nav-jump:open", (e) => {
      if (e.detail !== this && this.isOpen) this.close();
    });

    document.addEventListener("nav-sticky:unstuck", () => {
      if (this.isOpen) this.close();
    });
  }
}
